import React from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { ZinIcon } from '@tnl/zin-react'
import { StepName } from '@account/components/Register/types'
import { useRegister } from '@account/hooks/useRegister'
import { REGISTRATION_STEPS_BY_NAME, getStepNames, getStepIndexByName } from '@account/components/Register/stepRegistry'
import { SIGNIN_STEPS, getStepOrder as getSignInStepOrder } from '@account/components/SignIn/stepRegistry'
import type { SignInStepName } from '@account/components/SignIn/types'
import { SignInContext } from '@account/contexts/signin'
import { useModal } from '@hooks/useModal'
import { clientUrl, serverUrl } from '@utils/MkEnv'
import { MkSwitch } from '../../components/ui/Switch'
import { useDevUi } from './DevUiProvider'
import './DevUiPanel.scss'
// Tailwind-styled panel (no Zin components)

// System information
const makoVersion = process.env.npm_package_version || '1.3.6'
const tnlStack = process.env.TNL_STACK || 'N/A'
const nodeEnv = process.env.NODE_ENV || 'development'

// Type for rolodex items
interface RolodexStep {
  key: string
  stepName: string
  label: string
  description: string
  flow: 'register' | 'signin'
}

export const DevUiPanel: React.FC<{ className?: string }> = ({ className }) => {
  const dev = useDevUi()
  const registration = useRegister()
  const { openRegisterModal, openSignInModal } = useModal({})
  
  // Safely get signin context (returns null if not available - modal not open)
  const signInContext = React.useContext(SignInContext)
  const [showRolodex, setShowRolodex] = React.useState(false)
  const [selectedFlow, setSelectedFlow] = React.useState<'register' | 'signin'>('register')
  const panelRef = React.useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = React.useState<{ x: number; y: number }>({ x: 12, y: 12 })
  const [drag, setDrag] = React.useState<{ dx: number; dy: number; dragging: boolean }>({ dx: 0, dy: 0, dragging: false })
  const [minimized, setMinimized] = React.useState(false)

  // Register rolodex data - dynamically generated from registry
  const registerRolodex = React.useMemo<RolodexStep[]>(() => {
    const rolodex = getStepNames().map((stepName, index) => {
      const step = REGISTRATION_STEPS_BY_NAME[stepName]
      return {
        key: step.name, // Use step.name (StepName enum) instead of step.key for consistency
        stepName: step.name, // Store the actual StepName enum value
        label: `${index + 1}. ${step.title}`,
        description: step.content?.description || step.title,
        flow: 'register' as const,
      }
    })
    return rolodex
  }, [])

  // SignIn rolodex data - dynamically generated from SignIn registry
  const signinRolodex = React.useMemo<RolodexStep[]>(() => {
    const stepOrder = getSignInStepOrder()
    return stepOrder.map((stepName, index) => {
      const step = SIGNIN_STEPS.get(stepName)
      return {
        key: stepName, // Use stepName directly for consistency
        stepName, // Store the actual step name
        label: `${index + 1}. ${step?.title || stepName}`,
        description: step?.content?.description || step?.title || stepName,
        flow: 'signin' as const,
      }
    })
  }, [])

  // Get the appropriate rolodex based on selected flow
  const currentRolodex = React.useMemo(() => (selectedFlow === 'register' ? registerRolodex : signinRolodex), [selectedFlow, registerRolodex, signinRolodex])
  const [selectedStepKey, setSelectedStepKey] = React.useState<string>(currentRolodex[0]?.stepName || '')

  // Update selected step when flow changes (only when flow changes, not rolodex)
  React.useEffect(() => {
    const firstStepName = currentRolodex[0]?.stepName || ''
    setSelectedStepKey(firstStepName)
  }, [selectedFlow]) // Removed currentRolodex dependency to prevent unwanted resets

  // Function to navigate to a specific step in an already open modal
  const navigateToStep = React.useCallback(
    (stepKey: string, flow: 'register' | 'signin') => {
      if (flow === 'register') {
        const stepIndex = getStepIndexByName(stepKey as StepName)
        if (stepIndex !== undefined) {
          registration.setCurrentStep(stepKey as StepName)
        }
      } else if (flow === 'signin') {
        // For SignIn, navigate through context when modal is open
        if (signInContext) {
          signInContext.setCurrentStep(stepKey as SignInStepName)
        } else {
          // If modal is not open, open it at the selected step
          openSignInModal(undefined, undefined, stepKey as SignInStepName)
        }
      }
    },
    [registration, signInContext, openSignInModal],
  )

  // Function to open modal at selected step
  const handleOpenModal = React.useCallback(() => {
    if (selectedFlow === 'register') {
      openRegisterModal(undefined, selectedStepKey as StepName)
    } else {
      openSignInModal(undefined, undefined, selectedStepKey as SignInStepName)
    }
  }, [selectedFlow, selectedStepKey, openRegisterModal, openSignInModal])

  const indicatorColor = dev.enabled ? '#1db954' : '#9ca3af'

  const onMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('.register-dev-panel__interactive, button, input, select, [role="switch"]')) {
      return
    }
    const rect = panelRef.current?.getBoundingClientRect()
    setDrag({ dx: rect ? e.clientX - rect.left : 0, dy: rect ? e.clientY - rect.top : 0, dragging: false })
  }

  // Keyboard handler for Shift+D to toggle minimized state
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Only handle Shift+D if not in an input field, textarea, or contenteditable element
      const target = e.target as HTMLElement
      const isInInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true' ||
        target.closest('input, textarea, [contenteditable="true"]') ||
        target.closest('[data-testid*="input"]') ||
        target.closest('[id*="username"]') ||
        target.closest('[id*="password"]') ||
        target.closest('[id*="email"]') ||
        target.closest('[name*="username"]') ||
        target.closest('[name*="password"]') ||
        target.closest('[name*="email"]') ||
        target.closest('.zin-input') ||
        target.closest('.zin-input') || // This should work IF zin-input is rendered correctly
        target.closest('zin-input') || // Add support for shadow DOM or custom elements
        target.closest('[role="textbox"]')

      if (e.shiftKey && (e.key === 'D' || e.key === 'd') && !isInInput) {
        e.preventDefault()
        setMinimized((m) => !m)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  React.useEffect(() => {
    if (!drag.dragging) return undefined
    const handleMove = (e: MouseEvent) => {
      const x = Math.max(8, e.clientX - drag.dx)
      const y = Math.max(8, e.clientY - drag.dy)
      setPos({ x, y })
    }
    const handleUp = () => setDrag((d) => ({ ...d, dragging: false }))
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
  }, [drag.dragging, drag.dx, drag.dy])

  const tooltipText = `Dev Mode: ${dev.enabled ? 'On' : 'Off'}\nBypass: ${dev.bypassValidation ? 'On' : 'Off'}\nFlow: ${selectedFlow}\nStep: ${selectedStepKey}${minimized ? '\nMinimized: Yes' : ''}`

  const containerEl = React.useMemo<HTMLElement>(() => {
    try {
      const el = document.querySelector(dev.panelContainerSelector)
      return (el as HTMLElement) || document.body
    } catch (_) {
      return document.body
    }
  }, [dev.panelContainerSelector])

  const panel = (
    <div
      ref={panelRef}
      className={clsx('register-dev-panel', minimized && 'register-dev-panel--minimized', className)}
      style={{ left: pos.x, top: pos.y }}
      title={tooltipText}
      aria-label="Developer panel">
      {/* Header row: drag handle + title + minimize */}
      <div
        className="register-dev-panel__bar"
        onMouseDown={onMouseDown}
        title={tooltipText}
        role="toolbar"
        aria-label="Developer panel toolbar">
        <span className="dev-panel-title">Developer Panel{minimized && ' (Minimized)'}</span>
        <button
          type="button"
          className="register-dev-panel__toggle-btn register-dev-panel__interactive"
          onClick={() => setMinimized((v) => !v)}
          aria-label={minimized ? 'Expand Dev Panel' : 'Minimize Dev Panel'}
          title={minimized ? 'Expand Dev Panel' : 'Minimize Dev Panel'}>
          <ZinIcon
            name={minimized ? 'chevron-down' : 'minus'}
            set="fas"
          />
        </button>
      </div>

      {!minimized && (
        <div
          className="register-dev-panel__content"
          title={tooltipText}>
          {/* System Information */}
          <div className="dev-panel-system-info register-dev-panel__interactive">
            <h3 className="dev-panel-system-info__title">System Info</h3>
            <div className="dev-panel-system-info__grid">
              <div className="dev-panel-system-info__item">
                <span className="dev-panel-system-info__label">Mako version:</span>
                <span className="dev-panel-system-info__value">{makoVersion}</span>
              </div>
              <div className="dev-panel-system-info__item">
                <span className="dev-panel-system-info__label">T+L Stack:</span>
                <span className="dev-panel-system-info__value">{tnlStack}</span>
              </div>
              <div className="dev-panel-system-info__item">
                <span className="dev-panel-system-info__label">Client URL:</span>
                <span className="dev-panel-system-info__value">{clientUrl}</span>
              </div>
              <div className="dev-panel-system-info__item">
                <span className="dev-panel-system-info__label">Server URL:</span>
                <span className="dev-panel-system-info__value">{serverUrl}</span>
              </div>
              <div className="dev-panel-system-info__item">
                <span className="dev-panel-system-info__label">NODE_ENV:</span>
                <span className="dev-panel-system-info__value">{nodeEnv}</span>
              </div>
            </div>
          </div>

          {/* Control toggles */}
          <div className="dev-panel-toggles register-dev-panel__interactive">
            <div className="dev-panel-toggle-item">
              <MkSwitch
                inputId="dev-enabled"
                checked={dev.enabled}
                onChange={(e: any) => dev.setEnabled(!!e.value)}
              />
              <span className="dev-panel-toggle-label">Dev Mode</span>
            </div>
            <div className="dev-panel-toggle-item">
              <MkSwitch
                inputId="dev-bypass-validation"
                checked={dev.bypassValidation}
                onChange={(e: any) => dev.setBypassValidation(!!e.value)}
              />
              <span className="dev-panel-toggle-label">Bypass Validation</span>
            </div>
          </div>

          {/* Flow Selector */}
          <div className="dev-panel-flow-selector register-dev-panel__interactive">
            <label
              htmlFor="dev-flow-selector"
              className="dev-panel-label">
              Flow
            </label>
            <select
              id="dev-flow-selector"
              value={selectedFlow}
              onChange={(e) => setSelectedFlow(e.target.value as 'register' | 'signin')}>
              <option value="register">Register</option>
              <option value="signin">Sign In</option>
            </select>
          </div>

          {/* Clear Storage Button */}
          <div className="dev-panel-clear-storage register-dev-panel__interactive">
            <button
              type="button"
              onClick={() => {
                // Clear all registration-related storage
                localStorage.removeItem('mk-registration-context')
                localStorage.removeItem('mfaFactors')
                localStorage.removeItem('stateToken')
                localStorage.removeItem('mfaNextLink')
                localStorage.removeItem('mfaFactorId')
                sessionStorage.removeItem('mfaSetupData')
                sessionStorage.removeItem('registrationContext')
              }}>
              Clear Registration Storage
            </button>
          </div>

          {/* Open Modal Button */}
          <div className="dev-panel-open-modal register-dev-panel__interactive">
            <button
              type="button"
              onClick={handleOpenModal}>
              Open {selectedFlow === 'register' ? 'Register' : 'Sign In'} Modal
            </button>
          </div>

          {/* Step Navigation Rolodex */}
          <div className="dev-panel-steps register-dev-panel__interactive">
            <div className="dev-panel-steps-header">
              <h3>Step Navigation</h3>
              <button
                type="button"
                onClick={() => setShowRolodex(!showRolodex)}>
                {showRolodex ? 'Hide' : 'Show'} Steps
              </button>
            </div>

            {showRolodex && (
              <div className="dev-panel-steps-list custom-scrollbar">
                {currentRolodex.map((step) => {
                  const isSelected = step.stepName === selectedStepKey
                  return (
                    <button
                      key={step.key}
                      type="button"
                      className={clsx('dev-panel-step-button', isSelected && 'dev-panel-step-button--selected')}
                      onClick={() => {
                        setSelectedStepKey(step.stepName)
                        navigateToStep(step.stepName, step.flow)
                      }}>
                      <div className="dev-panel-step-label">{step.label}</div>
                      <div className="dev-panel-step-description">{step.description}</div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status Indicator */}
      <div
        className={clsx('register-dev-panel__indicator', dev.enabled && 'dev-panel-indicator--active')}
        style={{
          backgroundColor: indicatorColor,
        }}
        title={tooltipText}
      />
    </div>
  )

  return dev.uiVisible ? createPortal(panel, containerEl) : null
}

export default DevUiPanel
